import React from 'react';
import { ProjectTimelineItem } from '../types';
import Icon from '../../../components/AppIcon';

interface DevelopmentTimelineProps {
  timeline: ProjectTimelineItem[];
}

const DevelopmentTimeline = ({ timeline }: DevelopmentTimelineProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'current':
        return 'bg-blue-500';
      case 'upcoming':
        return 'bg-gray-300';
      default:
        return 'bg-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'current':
        return 'Clock';
      case 'upcoming':
        return 'Circle';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="bg-surface rounded-xl p-6 lg:p-8 elevation-2 mb-8">
      <h2 className="text-2xl font-bold text-text-primary mb-6">Development Timeline</h2>

      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>

        <div className="space-y-8">
          {timeline.map((item, index) => (
            <div key={item.id} className="relative flex items-start space-x-4">
              {/* Timeline Dot */}
              <div className={`relative z-10 w-12 h-12 rounded-full ${getStatusColor(item.status)} flex items-center justify-center flex-shrink-0`}>
                <Icon
                  name={getStatusIcon(item.status)}
                  size={20}
                  className="text-white"
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {item.title}
                    </h3>
                    <p className="text-sm text-accent font-medium">
                      {item.phase}
                    </p>
                  </div>
                  <div className="text-sm text-text-secondary mt-1 sm:mt-0">
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <Icon name="Calendar" size={14} className="mr-1" />
                        {item.date}
                      </span>
                      <span className="flex items-center">
                        <Icon name="Clock" size={14} className="mr-1" />
                        {item.duration}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-text-secondary mb-4 leading-relaxed">
                  {item.description}
                </p>

                {item.deliverables.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-text-primary mb-2">
                      Key Deliverables:
                    </h4>
                    <ul className="space-y-1">
                      {item.deliverables.map((deliverable, deliverableIndex) => (
                        <li key={deliverableIndex} className="flex items-start text-sm text-text-secondary">
                          <Icon name="ArrowRight" size={14} className="mr-2 mt-0.5 text-accent flex-shrink-0" />
                          {deliverable}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DevelopmentTimeline;